/**
 * Converts HEIC or TIFF files to JPEG so they can be previewed in the browser
 * and uploaded to S3 in a universally supported format.
 * All other formats are returned unchanged.
 */
export async function convertToJpeg(file) {
    const name = file.name.toLowerCase();
    const isHeic = file.type === "image/heic" || name.endsWith(".heic");
    const isTiff = file.type === "image/tiff" || name.endsWith(".tiff") || name.endsWith(".tif");

    if (!isHeic && !isTiff) return file;

    const baseName = file.name.replace(/\.[^.]+$/, "") + ".jpg";

    if (isHeic) {
        const heic2any = (await import("heic2any")).default;
        const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
        const blob = Array.isArray(result) ? result[0] : result;
        return new File([blob], baseName, { type: "image/jpeg" });
    }

    // TIFF: decode with utif, render to canvas, export as JPEG
    const UTIF = await import("utif");
    const buffer = await file.arrayBuffer();
    const ifds = UTIF.decode(buffer);
    if (!ifds.length) throw new Error("Failed to decode TIFF: no pages found");
    UTIF.decodeImage(buffer, ifds[0]);
    const rgba = UTIF.toRGBA8(ifds[0]);
    const { width, height } = ifds[0];
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba.buffer), width, height), 0, 0);
    const blob = await new Promise((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Canvas toBlob failed"))), "image/jpeg", 0.9)
    );
    return new File([blob], baseName, { type: "image/jpeg" });
}
